<template>
  <div class="help-page">
    <div v-if="selectedContractName" class="contract-breadcrumb">
      <span class="breadcrumb-label">{{ t('common.labels.contractBreadcrumbLabel') }} :</span>
      <router-link
        v-if="selectedContractLink"
        class="breadcrumb-value"
        :to="selectedContractLink"
      >
        {{ selectedContractName }}
      </router-link>
      <span v-else class="breadcrumb-value">{{ selectedContractName }}</span>
    </div>
    <header class="help-header">
      <div>
        <h1>{{ tHelp('title') }}</h1>
        <p>{{ tHelp('subtitle') }}</p>
      </div>
    </header>

    <div
      v-if="isAccountActive"
      class="steps-indicator"
    >
      <div :class="['step-pill', { active: currentStep >= 1 }]">
        <span>1</span>{{ tHelp('steps.chooseContract') }}
      </div>
      <div :class="['step-pill', { active: currentStep >= 2 }]" :aria-disabled="!selectedContractSupportsBrainSearch">
        <span>2</span>{{ tHelp('steps.askBrain') }}
      </div>
      <div
        :class="['step-pill', { active: currentStep >= 3 }]"
        :aria-disabled="selectedContractSupportsBrainSearch && !latestBrainRequestId"
      >
        <span>3</span>{{ tHelp('steps.createSupport') }}
      </div>
    </div>
    <div
      v-else
      class="help-section account-locked"
    >
      <v-icon size="36" color="#EA5545" class="locked-icon">mdi-lock-alert</v-icon>
      <div>
        <h2>{{ tHelp('accountLocked.title') }}</h2>
        <p>{{ tHelp('accountLocked.description') }}</p>
      </div>
    </div>

    <section
      v-if="isAccountActive && !selectedContract"
      class="help-section"
    >
      <div class="section-header">
        <h2>{{ tHelp('sections.contracts.title') }}</h2>
        <p>{{ tHelp('sections.contracts.subtitle') }}</p>
      </div>
      <div v-if="isLoading.contracts" class="section-loader">
        <v-progress-circular indeterminate color="#EA5545" size="32" />
      </div>
      <div v-else-if="contracts.length > 0" class="contracts-grid">
        <v-card
          v-for="contract in contracts"
          :key="contract.myp_id"
          class="contract-card"
          :elevation="selectedContract?.myp_id === contract.myp_id ? 4 : 1"
          @click="selectContract(contract)"
        >
          <v-card-text>
            <div class="contract-card-header">
              <div>
                <h3>{{ contract.prod_name || tHelp('labels.unknownProduct') }}</h3>
                <p>#{{ contract.myp_id }} · {{ contract.myp_product_id }}</p>
              </div>
              <v-icon color="#EA5545">
                {{ selectedContract?.myp_id === contract.myp_id ? 'mdi-check-circle' : 'mdi-circle-outline' }}
              </v-icon>
            </div>
            <p class="contract-meta" v-if="contract.myp_created_date">
              <v-icon size="14" class="mr-1">mdi-calendar</v-icon>
              {{ formatDate(contract.myp_created_date) }}
            </p>
          </v-card-text>
        </v-card>
      </div>
      <EmptyState
        v-else
        icon="mdi-file-alert"
        :title="tHelp('sections.contracts.emptyTitle')"
        :description="tHelp('sections.contracts.emptyDescription')"
      />
    </section>
    <section
      v-else-if="isAccountActive"
      class="help-section contract-selected-banner"
    >
      <div class="section-header">
        <h2>1. Clearment Vertragsschutz für {{ selectedContractName }}</h2>
        <p>Dein Vertrag ist ausgewählt – fahre mit den nächsten Schritten fort.</p>
      </div>
    </section>

    <section
      v-if="isAccountActive && selectedContract && selectedContractSupportsBrainSearch && !latestBrainRequestId"
      class="help-section"
    >
      <div class="section-header">
        <h2>{{ tHelp('sections.brain.title') }}</h2>
        <p>{{ tHelp('sections.brain.subtitle', { name: selectedContract.prod_name || '#' + selectedContract.myp_id }) }}</p>
      </div>
      <BrainSearch
        :showHistory="false"
        :presetProductId="selectedContract.myp_product_id"
        :lockProductSelection="true"
        @search-completed="handleBrainSearch"
      />
    </section>

    <section
      v-if="isAccountActive && (latestBrainRequestId || !selectedContractSupportsBrainSearch)"
      class="help-section"
    >
      <div
        v-if="latestBrainRequestId"
        class="brain-summary"
      >
        <div>
          <label>{{ tHelp('labels.question') }}</label>
          <p>{{ latestPrompt }}</p>
        </div>
        <div v-if="latestResult">
          <label>{{ tHelp('labels.answer') }}</label>
          <p>{{ latestResult }}</p>
        </div>
      </div>
      <div class="section-header">
        <h2>{{ tHelp('sections.support.title') }}</h2>
        <p>{{ tHelp('sections.support.subtitle') }}</p>
      </div>

      <p
        v-if="!selectedContractSupportsBrainSearch"
        class="content-text mb-4"
      >
        {{ tHelp('sections.brain.unavailableAction') }}
      </p>

      <div class="support-form">
        <v-text-field
          v-model="supportForm.title"
          :label="tHelp('form.titleLabel')"
          variant="outlined"
          class="mb-4"
        />
        <v-textarea
          v-model="supportForm.description"
          :label="tHelp('form.descriptionLabel')"
          variant="outlined"
          rows="4"
          class="mb-4"
        />
        <div class="form-actions">
          <v-btn
            color="#EA5545"
            variant="flat"
            :loading="isCreatingSupport"
            :disabled="isCreatingSupport || !supportForm.title || !supportForm.description"
            @click="createSupportTicket"
          >
            <v-icon left>mdi-ticket</v-icon>
            {{ tHelp('form.submit') }}
          </v-btn>
        </div>
      </div>
    </section>

    <section
      v-if="!isAccountActive || latestBrainRequestId"
      class="help-section"
    >
      <div class="admin-list">
        <h3>{{ tHelp('sections.admins.title') }}</h3>
        <div v-if="isLoading.admins" class="section-loader">
          <v-progress-circular indeterminate color="#EA5545" size="24" />
        </div>
        <div v-else-if="companyAdmins.length > 0" class="admins-grid">
          <div v-for="admin in companyAdmins" :key="admin.user_id" class="admin-card">
            <div class="admin-avatar">
              <v-icon size="20" color="white">mdi-shield-account</v-icon>
            </div>
            <div>
              <strong>{{ admin.user_admin_name || admin.user_name }}</strong>
              <p>{{ admin.com_name  }}</p>
            </div>
          </div>
        </div>
        <EmptyState
          v-else
          icon="mdi-shield-alert"
          :title="tHelp('sections.admins.emptyTitle')"
          :description="tHelp('sections.admins.emptyDescription')"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, inject, onMounted, reactive, ref, watch } from 'vue'
import { VIcon } from 'vuetify/components'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import BrainSearch from '@/components/objects/BrainSearch.vue'
import { useLanguage } from '@/composables/useLanguage'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const userStore = inject('userStore')
const errorStore = inject('errorStore')
const successStore = inject('successStore')
const router = useRouter()
const route = useRoute()
const { t } = useLanguage()
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)
const HELP_TRANSLATION_PREFIX = 'home.help'
const tHelp = (path, replacements) => t(`${HELP_TRANSLATION_PREFIX}.${path}`, replacements)
const { userData } = storeToRefs(userStore)
const userStatus = computed(() => Number(userData.value?.user_status ?? 0))
const isAccountActive = computed(() => userStatus.value === 1)

const contracts = ref([])
const companyAdmins = ref([])
const selectedContract = ref(null)
const latestBrainRequestId = ref(null)
const latestPrompt = ref('')
const latestResult = ref('')
const contractsLoaded = ref(false)
const EmptyState = defineComponent({
  name: 'EmptyState',
  props: {
    icon: { type: String, required: true },
    title: { type: String, required: true },
    description: { type: String, required: true }
  },
  setup(props) {
    return () =>
      h('div', { class: 'state-block', 'data-state': 'empty' }, [
        h(
          VIcon,
          { size: 40, color: '#ccc' },
          { default: () => props.icon }
        ),
        h('h4', null, props.title),
        h('p', null, props.description)
      ])
  }
})

const supportForm = reactive({
  title: '',
  description: ''
})
const isLoading = reactive({
  contracts: false,
  admins: false
})
const isCreatingSupport = ref(false)
const isFetchingLogin = ref(false)

const currentStep = ref(1)

const selectedContractName = computed(() => {
  if (!selectedContract.value) return ''
  return selectedContract.value.prod_name || `#${selectedContract.value.myp_id}`
})

const selectedContractLink = computed(() => {
  if (!selectedContract.value?.myp_id) {
    return null
  }
  return {
    name: 'vertrag',
    params: { id: selectedContract.value.myp_id }
  }
})

function contractSupportsBrainSearch(contract) {
  if (!contract) return false
  return Number(contract.prod_brain_search || 0) === 1
}

const selectedContractSupportsBrainSearch = computed(() =>
  contractSupportsBrainSearch(selectedContract.value)
)

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return t('common.labels.notAvailable')
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return dateString
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function selectContract(contract, syncRoute = true) {
  if (!contract) return
  selectedContract.value = contract
  currentStep.value = contractSupportsBrainSearch(contract) ? 2 : 3
  latestBrainRequestId.value = null
  latestPrompt.value = ''
  latestResult.value = ''
  supportForm.title = ''
  supportForm.description = ''

  if (!contractSupportsBrainSearch(contract)) {
    const contractLabel = contract.prod_name || (contract.myp_id ? `#${contract.myp_id}` : '')
    if (contractLabel) {
      supportForm.title = `Frage zu ${contractLabel}`
    }
  }

  if (
    syncRoute &&
    Number(route.params.id || 0) !== Number(contract.myp_id)
  ) {
    router.replace({
      name: 'help',
      params: { id: contract.myp_id },
      query: route.query
    })
  }
}

function applyRouteSelection() {
  if (!contracts.value || contracts.value.length === 0) {
    return
  }

  const routeId = Number(route.params.id || 0)
  if (routeId > 0) {
    if (
      selectedContract.value &&
      Number(selectedContract.value.myp_id) === routeId
    ) {
      return
    }
    const match = contracts.value.find(
      (contract) => Number(contract.myp_id) === routeId
    )
    if (match) {
      selectContract(match, false)
      return
    }
  }

  if (!selectedContract.value) {
    selectContract(contracts.value[0], false)
  }
}

function handleBrainSearch(payload) {
  latestBrainRequestId.value = payload?.requestId || null
  latestPrompt.value = payload?.prompt || ''
  latestResult.value = payload?.result || ''
  const contractLabel =
    selectedContract.value?.prod_name ||
    (selectedContract.value?.myp_id ? `#${selectedContract.value.myp_id}` : '')
  if (contractLabel) {
    supportForm.title = `Frage zu ${contractLabel}`
  } else {
    supportForm.title = latestPrompt.value || ''
  }
  supportForm.description = latestPrompt.value
  if (!supportForm.description) {
    supportForm.description = ''
  }
  if (latestBrainRequestId.value) {
    currentStep.value = 3
  }
}

async function loadContracts() {
  isLoading.contracts = true
  contractsLoaded.value = false
  try {
    const response = await baseCalls.clearmentPost({ action: 'getmyproducts' })
    if (response.data.success) {
      contracts.value = response.data.products || []
    }
  } catch (err) {
    console.error('Error loading contracts', err)
  } finally {
    isLoading.contracts = false
    contractsLoaded.value = true
    applyRouteSelection()
  }
}

async function loadCompanyAdmins() {
  isLoading.admins = true
  try {
    const response = await baseCalls.userPost({ action: 'getcompanyadmins' })
    if (response.data.success) {
      companyAdmins.value = response.data.admins || []
    }
  } catch (err) {
    console.error('Error loading admins', err)
  } finally {
    isLoading.admins = false
  }
}

async function createSupportTicket() {
  if (!selectedContract.value) return
  isCreatingSupport.value = true
  try {
    const payload = {
      action: 'insertsupport',
      sup_title: supportForm.title,
      sup_text: supportForm.description,
      sup_product_id: selectedContract.value.myp_product_id || 0
    }
    if (latestBrainRequestId.value) {
      payload.sup_request_id = latestBrainRequestId.value
    }
    const response = await baseCalls.clearmentPost(payload)

    if (response.data.success) {
      successStore?.setSuccess(
        true,
        tHelp('notifications.supportCreatedTitle'),
        tHelp('notifications.supportCreatedBody')
      )
      router.push({ name: 'support', params: { id: response.data.support?.sup_id } })
    } else {
      errorStore?.setError?.(
        true,
        response.data.message || tHelp('notifications.supportError')
      )
    }
  } catch (err) {
    console.error('Error creating support ticket', err)
    errorStore?.setError?.(true, tHelp('notifications.supportError'))
  } finally {
    isCreatingSupport.value = false
  }
}

async function ensureLoginData() {
  if (userData.value || isFetchingLogin.value) return
  isFetchingLogin.value = true
  try {
    const response = await baseCalls.userGet({ action: 'loggeduser', id: 0 })
    if (response.data?.user) {
      userStore?.setUser?.(response.data.user)
    }
  } catch (err) {
    console.error('Error loading login data', err)
  } finally {
    isFetchingLogin.value = false
  }
}

onMounted(async () => {
  await ensureLoginData()
  if (userData.value?.user_admin === 1) {
    router.replace({ name: 'adminindex' })
    return
  }
  loadContracts()
  loadCompanyAdmins()
})

watch(
  () => route.params.id,
  () => {
    if (contractsLoaded.value) {
      applyRouteSelection()
    }
  }
)
</script>

<style scoped src="@/assets/styles/sfc/components/sites/HelpIndex.css"></style>
